import { isDemo } from "../App";
import { axiosRequest } from "../axiosConfig";
import preCachedData from "./data.json";

class FaxCacheManager {
  constructor() {
    this.cache = new Map();
    this.maxCacheSize = 50;
    this.storageKey = "processedFaxData";
  }

  async readFromCache(faxId, isChild = false, childId = null) {
    try {
      // Create the appropriate cache key
      console.log("Fax Id: ", faxId)
      const dataKey = isChild ? `${faxId}_${childId}` : `${faxId}`;
      console.log("Data Key: ", dataKey);
      const cacheKey = `${dataKey}_fax`;

      // Check pre-cached data first
      if (preCachedData && preCachedData[dataKey]?.processed_fax_data) {
        console.log(`Found fax data in pre-cached JSON: ${dataKey}`);
        return preCachedData[dataKey].processed_fax_data;
      }

      // Check in-memory cache
      if (this.cache.has(cacheKey)) {
        console.log(`Found fax data in memory cache: ${cacheKey}`);
        return this.cache.get(cacheKey).processed_fax_data;
      }

      // Check session storage
      if (typeof sessionStorage !== "undefined") {
        const storedCache = sessionStorage.getItem(this.storageKey);
        if (storedCache) {
          const parsedCache = JSON.parse(storedCache);
          if (parsedCache[cacheKey]) {
            console.log(`Found fax data in session storage: ${cacheKey}`);
            // Add to in-memory cache for faster access next time
            this.cache.set(cacheKey, parsedCache[cacheKey]);
            return parsedCache[cacheKey].processed_fax_data;
          }
        }
      }

      return null;
    } catch (error) {
      console.error("Error reading from fax cache:", error);
      return null;
    }
  }

  async writeToCache(faxId, data, isChild = false, childId = null) {
    try {
      const dataKey = isChild ? `${faxId}_${childId}` : faxId;
      const cacheKey = `${dataKey}_fax`;

      const cacheEntry = {
        processed_fax_data: data,
        timestamp: new Date().toISOString(),
      };

      // Update in-memory cache
      this.cache.set(cacheKey, cacheEntry);

      // Maintain cache size limit
      if (this.cache.size > this.maxCacheSize) {
        const oldestKey = this.cache.keys().next().value;
        this.cache.delete(oldestKey);
      }

      // Update persistent storage
      if (typeof sessionStorage !== "undefined") {
        try {
          const persistentCache = {};
          this.cache.forEach((value, key) => {
            persistentCache[key] = value;
          });
          sessionStorage.setItem(
            this.storageKey,
            JSON.stringify(persistentCache),
          );
          console.log(`Cache updated in sessionStorage: ${cacheKey}`);
        } catch (err) {
          console.log("Error writing to persistent cache:", err);
        }
      }
    } catch (error) {
      console.error("Error writing to cache:", error);
    }
  }

  async fetchProcessedFax(url, faxId, isChild = false, childId = null) {
    try {
      // Try to get from cache first
      const cachedData = await this.readFromCache(faxId, isChild, childId);
      if (isDemo && cachedData) {
        console.log(`Using cached fax data for demo mode: ${isChild ? `${faxId}_${childId}` : faxId}`);
        return cachedData;
      }

      // If not in cache or not in demo mode, fetch from API
      console.log(`Fetching processed fax data from API: ${url}`);
      const response = await axiosRequest.get(url);

      // Check for error response
      if (typeof response.data === "string" && response.data.length > 2) {
        throw new Error(response.data);
      }

      // Cache the response
      await this.writeToCache(faxId, response.data, isChild, childId);

      return response.data;
    } catch (error) {
      console.error("Error fetching processed fax:", error);

      // Check specific error types
      if (error?.response?.data?.length < 2) {
        throw new Error(error?.response?.data[0]?.Message);
      } else {
        throw new Error(error?.response?.data || error.message);
      }
    }
  }
}

// Export singleton instance
export const faxCacheManager = new FaxCacheManager();